import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FRIDGE_TILES } from "../../lib/fridgeTiles";
import { PANTRY_TILES } from "../../lib/pantryTiles";
import { FREEZER_TILES } from "../../lib/freezerTiles";
import { buildDisplayName } from "./helpers";
import { font, color } from "./tokens";

const SRC = {
  manual:  "/img/manual-success.png",
  scanner: "/img/scanner-success.png",
};

const COUNTDOWN_SECONDS = 3;

const titleCase = (s) =>
  typeof s === "string" && s.length
    ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ")
    : "";

const TILES_BY_LOCATION = {
  fridge: FRIDGE_TILES,
  pantry: PANTRY_TILES,
  freezer: FREEZER_TILES,
};

function buildPathLabel(row) {
  if (!row) return "";
  const loc = titleCase(row.location);
  const tileList = TILES_BY_LOCATION[row.location] || [];
  const tile = tileList.find(t => t.id === row.tileId);
  const tileLabel = tile?.label || "";
  const cat = titleCase(row.category);
  return [loc, tileLabel, cat].filter(Boolean).join(" → ");
}

export default function SuccessOverlay({ mode, row, onDone }) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onDone && onDone();
      return;
    }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onDone]);

  const src = SRC[mode];
  if (!src) return null;

  const name = buildDisplayName(row);
  const path = buildPathLabel(row);

  return (
    <motion.div
      key={mode}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      onClick={() => onDone && onDone()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 600,
        background: "rgba(12, 10, 8, 0.78)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 360,
          background: color.paper,
          color: color.ink,
          borderRadius: 20,
          padding: 20,
          boxShadow: "0 24px 60px rgba(0,0,0,0.45), 0 2px 0 rgba(255,255,255,0.6) inset",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 14,
          cursor: "default",
        }}
      >
        <div
          style={{
            width: "100%",
            aspectRatio: "1024 / 1536",
            maxHeight: "44vh",
            borderRadius: 14,
            overflow: "hidden",
            background: color.parchment,
          }}
        >
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              userSelect: "none",
              display: "block",
            }}
          />
        </div>

        {(name || path) && (
          <div style={{ textAlign: "center", padding: "0 4px" }}>
            <div
              style={{
                fontFamily: font.detail,
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: 18,
                lineHeight: 1.3,
                color: color.ink,
              }}
            >
              Your{" "}
              <span
                style={{
                  fontFamily: font.itemTitle,
                  fontStyle: "italic",
                  color: color.warmBrown,
                }}
              >
                {name || "item"}
              </span>{" "}
              has been successfully stored in
            </div>
            {path && (
              <div
                style={{
                  marginTop: 8,
                  fontFamily: font.mono,
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: color.inkMuted,
                }}
              >
                {path}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => onDone && onDone()}
          style={{
            marginTop: 4,
            width: "100%",
            padding: "14px 18px",
            background: color.burnt,
            color: color.paper,
            border: "none",
            borderRadius: 12,
            fontFamily: font.mono,
            fontSize: 12,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <span>Continue</span>
          <span
            aria-hidden="true"
            style={{
              fontFamily: font.mono,
              fontSize: 12,
              letterSpacing: "0.04em",
              opacity: 0.85,
              minWidth: 16,
              textAlign: "center",
            }}
          >
            {Math.max(0, secondsLeft)}
          </span>
        </button>
      </motion.div>
    </motion.div>
  );
}
